import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  useMemo,
  useLayoutEffect,
} from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useForm, useFormState } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import AdyenCheckout from '@adyen/adyen-web';
import { ReactComponent as WarningIcon } from 'assets/icons/exclamation-circle.svg';
import { ReactComponent as Spinner } from 'assets/icons/spinner.svg';

import { ReactComponent as ChevronDown } from 'assets/icons/chevron-down.svg';
import { ReactComponent as ChevronUp } from 'assets/icons/chevron-up.svg';
import BillingAddress from './BillingAddress/BillingAddress';
import { StripeFormContainer } from './StripeForm/StripeFormContainer';

import { FIELD_NAMES, generateBillingAddressSchema } from './BillingAddress/BillingAddress.schema';
import { paymentMethod as TEXT } from 'locales/en/cart';
import { priceForAPI } from 'utils/price';
import { BASE_ADYEN_CONFIG } from 'utils/constants';
import { REQUEST_AUTH, SUCCESS, FAILED } from 'services/api/types';
import { showErrorMessage, showSuccessMessage } from 'redux/slices/notification';
import { POST_APPLE_PAY_SESSION } from 'services/api';
import { cleanCart, cartSelector, setStripeTotalCharge } from 'redux/slices/cart';
import {
  createOrder,
  getCheckoutPaymentMethods,
  setCheckoutError,
  clearOrderCreate,
  setShippingAddressDisabledState,
  checkoutStoreIntegrationSelector,
  setRenderStripeForm,
} from 'redux/slices/checkout';
import './adyen-overrides.css';
import { usePrevious } from 'hooks';
import countries from 'utils/countries.json';

const PaymentMethod = ({ storeId }) => {
  const dispatch = useDispatch();
  const redirectRef = useRef(null);
  const cardRef = useRef(null);
  const cart = useSelector(cartSelector.selectAll);
  const {
    shippingAddressDisabled,
    canPreviewStripeTerminalOrder,
    previewingTerminalOrder,
    checkoutStep,
    renderStripeForm,
  } = useSelector(state => state.checkout);

  useLayoutEffect(() => {
    dispatch(setShippingAddressDisabledState(false));
    const hasArrivedFromStripe = new URLSearchParams(window.location.search).get("payment_intent_client_secret");
    if (hasArrivedFromStripe) {
      dispatch(setRenderStripeForm(true));
    }
  }, [dispatch]);

  const [isExpanded, setIsExpanded] = useState(true);
  const [checkoutElement, setCheckoutElement] = useState(null);
  const [hideShippingAddress, setHideShippingAddress] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const defaultValues = useSelector(
    (state) => state.checkoutForm.billingAddress
  );
  const checkoutError = useSelector((state) => state.checkout.error);
  const selectedStoreIntegration = useSelector(checkoutStoreIntegrationSelector);
  const isStripeStore = selectedStoreIntegration !== 'adyen';

  const schema = useMemo(() => {
    return generateBillingAddressSchema(isStripeStore);
  }, [isStripeStore]);

  const { control, register, setValue, trigger, watch, setError, reset } = useForm({
    mode: 'onChange',
    resolver: yupResolver(schema),
    defaultValues,
  });

  useEffect(() => {
    if (isStripeStore) {
      const productCountry = cart.map(product => product?.productable.country)[0];
      setValue(FIELD_NAMES.country, countries.find(({ code }) => code === productCountry) || {});
    }
  }, [cart, isStripeStore, setValue]);

  const { errors } = useFormState({
    control,
  });

  const total = useSelector((state) => state.cart.charge);
  const { orderCreateResponse, checkoutPaymentMethods, store } = useSelector(
    (state) => state.checkout
  );

  useEffect(() => {
    if (!isStripeStore) {
      dispatch(getCheckoutPaymentMethods({ storeId }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const prevTotal = usePrevious(total);

  const amount = useMemo(() => {
    return {
      value: priceForAPI(total),
      currency: 'EUR',
    };
  }, [total]);

  const googlepayConfig = useMemo(() => {
    const configs = {
      buttonColor: 'white',
      buttonType: 'plain',
      environment:
        process.env.REACT_APP_ADYEN_ENV === 'live' ? 'PRODUCTION' : 'TEST',
      configuration: {
        gatewayMerchantId: process.env.REACT_APP_ADYEN_GATEWAY_MERCHANT_ID,
        merchantName: store.name,
      },
      onClick: async (resolve, reject) => {
        const isBillingValid = await trigger();
        if (!isBillingValid) {
          return;
        }
        return resolve();
      },
      transactionInfo: {
        countryCode: 'IE',
        totalPriceLabel: store.name,
      },
    };

    if (process.env.REACT_APP_ADYEN_ENV === 'live') {
      configs['configuration']['merchantId'] = store.google_pay_merchant_id;
    }

    return configs;
  }, [store.name, store.google_pay_merchant_id, trigger]);

  const applepayConfig = useMemo(() => {
    return {
      amount,
      buttonColor: 'white-with-line',
      buttonType: 'plain',
      totalPriceLabel: store.name,
      totalPriceStatus: 'final',
      countryCode: 'IE',
      lineItems: [],
      onValidateMerchant: (resolve, reject, validationURL) => {
        POST_APPLE_PAY_SESSION({
          validation_url: validationURL,
          initiative_context: window.location.host,
          display_name: store.name,
        })
          .then((response) => {
            resolve(response.data);
          })
          .catch((error) => {
            reject();
          });
      },
    };
  }, [amount, store.name]);

  const configuration = useMemo(() => {
    const onSubmit = async (adyenState, dropin) => {
      const isBillingValid = await trigger();

      if (!(isBillingValid && adyenState?.isValid)) return;
      dropin.setStatus('loading');
      return dispatch(
        createOrder({
          adyenData: adyenState.data,
          storeId,
        })
      ).then(() => dropin.setStatus('ready'));
    };
    return {
      ...BASE_ADYEN_CONFIG,
      paymentMethodsResponse: checkoutPaymentMethods,
      amount,
      onSubmit,
      locale: 'en-GB',
      translations: {
        'en-GB': {
          'error.va.sf-cc-dat.01': 'Card expired',
        },
      },
      paymentMethodsConfiguration: {
        card: {
          name: TEXT.addNewCard,
          hasHolderName: true,
          holderNameRequired: true,
          enableStoreDetails: false,
          styles: {
            base: {
              fontSize: '0.875rem',
              lineHeight: '0.875rem',
              fontSmoothing: 'antialiased',
            },
          },
        },
        paywithgoogle: googlepayConfig,
        applepay: applepayConfig,
      },
    };
  }, [
    amount,
    applepayConfig,
    checkoutPaymentMethods,
    dispatch,
    googlepayConfig,
    storeId,
    trigger,
  ]);

  useEffect(() => {
    if (isStripeStore) {
      return;
    }
    if (!checkoutPaymentMethods.paymentMethods?.length) return;
    if (!!total) {
      const createAdyenCheckout = async () => {
        const adyenCheckout = await AdyenCheckout(configuration);
        setCheckoutElement(adyenCheckout);
        adyenCheckout
          .create('dropin', {
            openFirstPaymentMethod: false,
            setStatusAutomatically: false,
            amount,
          })
          .mount(cardRef.current);
      };
      createAdyenCheckout();
    }
  }, [amount, checkoutPaymentMethods.paymentMethods?.length, configuration, isStripeStore, total]);

  useEffect(() => {
    if (!checkoutPaymentMethods.paymentMethods?.length) return;

    if (prevTotal !== total && !!checkoutElement) {
      checkoutElement?.update({ amount });
    }
  }, [
    amount,
    checkoutElement,
    checkoutPaymentMethods.paymentMethods?.length,
    prevTotal,
    total,
  ]);

  const afterOrderCreate = useCallback(() => {
    if (orderCreateResponse.status === REQUEST_AUTH) {
      checkoutElement
        .createFromAction(orderCreateResponse.action)
        .mount(redirectRef?.current);
    } else if (orderCreateResponse.status === SUCCESS) {
      dispatch(showSuccessMessage({ message: 'Order created!' }));
      dispatch(cleanCart(storeId));
      setTimeout(() => {
        window.location.replace(orderCreateResponse.return_url);
      }, 500);
    } else if (orderCreateResponse.status === FAILED) {
      window.scrollTo({
        top: 0,
        behavior: 'smooth',
      });
      dispatch(
        setCheckoutError(
          orderCreateResponse.error_description || TEXT.defaultError
        )
      );
    }
  }, [checkoutElement, dispatch, orderCreateResponse, storeId]);

  useEffect(() => {
    afterOrderCreate();
  }, [afterOrderCreate]);

  useEffect(() => {
    dispatch(setStripeTotalCharge(0))
  }, [dispatch]);

  useEffect(() => {
    return () => dispatch(clearOrderCreate());
  }, [dispatch]);

  const onClickExpand = () => {
    setIsExpanded(!isExpanded);
  };

  const handleProceedToStripeCheckout = async () => {
    await trigger();

    if (Object.keys(errors).length) {
      return;
    };

    dispatch(setRenderStripeForm(true));
    dispatch(setShippingAddressDisabledState(true));
  }

  const onStripeError = (error) => {
    const errorMessage = error.message;

    dispatch(showErrorMessage(errorMessage));

    if (errorMessage.includes('[')) {
      const fieldsToChange = errorMessage.slice(
        errorMessage.indexOf("[") + 1,
        errorMessage.indexOf("]")
      ).split(',');

      const allProperties = {
        [FIELD_NAMES.name]: 1,
        [FIELD_NAMES.address_1]: 1,
        [FIELD_NAMES.address_2]: 1,
        [FIELD_NAMES.postcode]: 1,
        [FIELD_NAMES.country]: 1,
        [FIELD_NAMES.billingOption]: 1,
        [FIELD_NAMES.billingAddressSameAsShipping]: 1,
        [FIELD_NAMES.shippingMethod]: 1,
        [FIELD_NAMES.city]: 1,
        [FIELD_NAMES.state]: 1,
        [FIELD_NAMES.billing_name]: 1,
        [FIELD_NAMES.billing_address_1]: 1,
        [FIELD_NAMES.billing_address_2]: 1,
        [FIELD_NAMES.billing_postcode]: 1,
        [FIELD_NAMES.billing_country]: 1,
        [FIELD_NAMES.billing_city]: 1,
        [FIELD_NAMES.billing_state]: 1
      };

      fieldsToChange.forEach(field => {
        const trimmedField = field.trim();
        if (allProperties[trimmedField]) {
          setError(trimmedField, { message: 'This field is invalid' });
        }
      });

      dispatch(setRenderStripeForm(false));
      return dispatch(setShippingAddressDisabledState(false));
    };

    setError(FIELD_NAMES.postcode, { message: 'This field is invalid' });
    setError(FIELD_NAMES.city, { message: 'This field is invalid' });

    dispatch(setShippingAddressDisabledState(false));
    dispatch(setRenderStripeForm(false));
  }

  const onStripeSuccess = () => {
    setHideShippingAddress(true);
  }

  const onBillingAddressChange = () => {
    dispatch(setRenderStripeForm(false))
    setHideShippingAddress(false);
    if (canPreviewStripeTerminalOrder && checkoutStep === 2) {
      return;
    }
    dispatch(setShippingAddressDisabledState(false));
  }

  return (
    <>
      {!!checkoutError && (
        <div className="border border-pink-600 bg-pink-50 h-20 lg:h-28 w-full rounded flex items-center px-5 lg:px-8 mb-4 lg:mb-8">
          <WarningIcon className="w-6 h-6 fill-current text-pink-600" />
          <div className="ml-5 lg:ml-8 text-javelin-gray-dark">
            <p className="font-bold text-sm lg:text-base">{checkoutError}</p>
            <p className="text-xs lg:text-sm">{TEXT.tryAgain}</p>
          </div>
        </div>
      )}
      <div ref={redirectRef} />
      <BillingAddress
        control={control}
        register={register}
        errors={errors}
        setValue={setValue}
        minimized={hideShippingAddress}
        reset={reset}
        storeId={storeId}
        onChangeClick={onBillingAddressChange}
        watch={watch}
      />
      {(isStripeStore && !renderStripeForm) && (
        <button
          className='btn btn-blue mt-4 mb-6 p-4 w-full max-w-none'
          onClick={handleProceedToStripeCheckout}
          data-testid="proceed-btn"
          disabled={!canPreviewStripeTerminalOrder || previewingTerminalOrder}
        >
          {previewingTerminalOrder ? 'Validating...' : 'Continue'}
        </button>
      )}
      {isLoading && (
        <Spinner className="animate-spin m-auto mt-4 mb-6 h-16 w-5 fill-current flex-shrink-0 text-javelin-blue-dark whitespace-nowrap" />
      )}
      {
        renderStripeForm && (
          <>
            <hr className="horizontal-line w-full hidden lg:block my-8" />
            <div
              className="flex justify-between items-center lg:pt-2"
              onClick={onClickExpand}
            >
              <h2
                data-testid="page-title"
                className="text-javelin-gray-dark text-sm lg:font-bold lg:text-2xl mb-1"
              >
                {TEXT.title}
              </h2>
              {isExpanded ? (
                <ChevronUp className="w-4 h-4 my-auto text-javelin-blue-dark fill-current cursor-pointer lg:hidden" />
              ) : (
                <ChevronDown className="w-4 h-4 my-auto text-javelin-blue-dark fill-current cursor-pointer lg:hidden" />
              )}
            </div>
            <div
              className={`lg:block lg:h-full transition-height ease-in-out ${isExpanded ? 'h-112 overflow-visible' : 'h-0 overflow-hidden'
                }`}
            >
              <div className="flex flex-col justify-between mt-2 lg:mt-0 pb-6 lg:pb-2">
                <div className="mx-0.5 lg:mx-0 transition-height ease-in-out h-auto">
                  <div id="dropin-container" ref={cardRef} />
                  <StripeFormContainer
                    watch={watch}
                    onError={onStripeError}
                    onSuccess={onStripeSuccess}
                    show={shippingAddressDisabled}
                    isLoading={isLoading}
                    setIsLoading={setIsLoading}
                  />
                </div>
              </div>
            </div>
          </>
        )
      }
    </>
  );
};

export default PaymentMethod;
